<template>
    <Modal ref="budgetmarkup" title="Budget Markup" size="is-large">
        <div v-if="budgetEntry" class="tw-grid tw-gap-3">
            <strong>{{ budgetEntry.name }}</strong>
            <div class="tw-flex tw-gap-3">
                <FormField label="Raised By">
                    {{ store.get('current_user').name }}
                </FormField>
                <FormField label="Budgeted Cost">
                    {{ budgetEntry.budgeted_cost }}
                </FormField>
                <FormField label="Markup Amount">
                    <PercentageValue
                        v-model="markupValue"
                        :base-value="budgetEntry.budgeted_cost"
                        :step="0.0001"
                        currency
                    />
                    <Checkbox v-model="applyToFixed" class="tw-mt-2">Apply markup to fixed amount</Checkbox>
                </FormField>
            </div>
            <div class="tw-grid tw-grid-cols-2 tw-gap-3">
                <FormField label="Cost Department Name">
                    {{ store.get('account.departments.' + budgetEntry.category?.department_id).name }}
                </FormField>
                <FormField label="Cost Department Category">
                    {{ budgetEntry.category?.name }}
                </FormField>
            </div>
            <div class="tw-grid tw-gap-3">
                <div class="tw-flex">
                    <FormField v-if="!creatingDepartment" label="Revenue Streams" class="tw-w-full">
                        <Select
                            v-model="markupDept"
                            placeholder="Select a Revenue Stream"
                            @update:model-value="markupCat = null"
                        >
                            <option v-for="item in revenueDepartments" :key="item.id" :value="item.id">
                                {{ item.name }}
                            </option>
                        </Select>
                    </FormField>
                    <FormField v-else label="New Revenue Stream" class="tw-w-full">
                        <Textbox v-model="markupDepartmentName" maxlength="255" />
                    </FormField>
                    <Tooltip
                        class="tw-ml-1"
                        position="left"
                        :label="
                            creatingDepartment
                                ? 'Switch to selecting an existing Revenue Stream'
                                : 'Switch to creating a new Revenue Stream'
                        "
                    >
                        <Button class="tw-mt-4" type="is-primary" @click="newDepartment">
                            {{ creatingDepartment ? 'Select' : 'New' }}
                        </Button>
                    </Tooltip>
                </div>
                <div class="tw-flex">
                    <FormField v-if="!creatingCategory" label="Revenue Categories" class="tw-w-full">
                        <Select v-model="markupCat" placeholder="Select a Revenue Category" :disabled="!markupDept">
                            <option v-for="item in categories" id="line-item" :key="item.id" :value="item.id">
                                {{ item.name }}
                            </option>
                        </Select>
                    </FormField>
                    <FormField v-else label="New Revenue Category" class="tw-w-full">
                        <Textbox v-model="markupCategoryName" maxlength="255" />
                    </FormField>
                    <Tooltip
                        position="left"
                        :label="
                            creatingCategory
                                ? 'Switch to creating a new subcategory'
                                : 'Switch to selecting an existing subcategory'
                        "
                        class="tw-ml-1"
                    >
                        <Button v-if="!creatingDepartment" class="tw-mt-4" type="is-primary" @click="newCategory">
                            {{ creatingCategory ? 'Select' : 'New' }}
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </div>
        <template #footer>
            <Button :disabled="!isValid" :loading type="is-success" @click="createRevenue">Create</Button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { useDataStore } from '@/js/stores/DataStore.js';
import Modal from '@/js/components/Modal.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Select from '@/js/components/controls/Select.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import { computed, ref, useTemplateRef } from 'vue';
import { axios } from '@/js/utils/axios.ts';
import { route } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import type { BudgetEntry } from '@/js/types/budgetEntry.ts';
import type { Department } from '@/js/types/department.ts';

const emit = defineEmits(['created']);
const budgetMarkup = useTemplateRef('budgetmarkup');
const store = useDataStore();
const budgetEntry = ref<null | BudgetEntry>(null);
const markupValue = ref<number | null>(null);
const markupDept = ref<number | null>(null);
const markupCat = ref<number | null>(null);
const markupDepartmentName = ref<string | null>(null);
const markupCategoryName = ref<string | null>(null);
const applyToFixed = ref(false);
const creatingDepartment = ref(false);
const creatingCategory = ref(false);
const loading = ref(false);
const revenueDepartments = computed(() => {
    return (Object.values(store.get('current_event.revenue_departments')) as Department[]).filter(
        (department) => department.revenue_stream_type !== 0
    );
});
const categories = computed(() => {
    if (markupDept.value) {
        return revenueDepartments.value.find((department) => department.id === markupDept.value)?.categories ?? [];
    }
    return store.get('current_event.categories');
});
const isValid = computed(() => {
    return (
        (markupDepartmentName.value || markupDept.value) &&
        (markupCategoryName.value || markupCat.value) &&
        markupValue.value &&
        markupValue.value >= 0
    );
});
const open = (payload: { donorEntry?: BudgetEntry; clientBudgeted?: number }) => {
    if (!payload || !payload.donorEntry) {
        return;
    }
    budgetEntry.value = payload.donorEntry;

    // Attempt to get default markup amount from category
    let defaultMarkupAmount = payload.donorEntry.category?.default_markup_amount;
    if (!defaultMarkupAmount) {
        // Fallback to event default markup
        defaultMarkupAmount = store.get('current_event.default_markup', 0);
    } else {
        // Fix for categories saving as whole numbers instead of decimal percentages
        defaultMarkupAmount = (parseFloat(defaultMarkupAmount) / 100).toString();
    }

    if (payload.donorEntry.category?.default_markup_destination) {
        const revenueDepartment = revenueDepartments.value.find((department) => {
            return !!Object.values(department.categories ?? {}).find(
                (c) => c.id === payload.donorEntry?.category?.default_markup_destination
            );
        });
        if (revenueDepartment) {
            // Unfortunately because these get set before vue has time to work out the valid options
            // we need to delay the setting of the markupDept and markupCat
            markupDept.value = revenueDepartment.id;
            markupCat.value = payload.donorEntry.category.default_markup_destination;
        }
    }
    if (payload.clientBudgeted !== undefined) {
        markupValue.value = Number((payload.clientBudgeted - Number(budgetEntry.value.budgeted_cost)).toFixed(2));
    } else if (defaultMarkupAmount) {
        markupValue.value = parseFloat(defaultMarkupAmount) * budgetEntry.value.budgeted_cost;
    }
    budgetMarkup.value?.open();
};
const close = () => {
    markupValue.value = 0;
    markupDept.value = 0;
    markupCat.value = 0;
    creatingDepartment.value = false;
    creatingCategory.value = false;
    applyToFixed.value = false;
    budgetMarkup.value?.close();
};
const newDepartment = () => {
    creatingDepartment.value = !creatingDepartment.value;
    if (creatingDepartment.value) {
        creatingCategory.value = true;
    }
    markupCat.value = null;
    markupDept.value = null;
    markupCategoryName.value = '';
};
const newCategory = () => {
    creatingCategory.value = !creatingCategory.value;
    markupCat.value = null;
    markupCategoryName.value = '';
};
const createRevenue = () => {
    if (!budgetEntry.value) {
        return;
    }
    // create formData
    loading.value = true;
    axios
        .post(route('api.account.event.entries.markup', { entry: budgetEntry.value.id }), {
            department_name: markupDepartmentName.value,
            department_id: markupDept.value,
            category_name: markupCategoryName.value,
            category_id: markupCat.value,
            markup_value: markupValue.value,
            apply_to_fixed: applyToFixed.value,
        })
        .then(
            () => {
                capturePosthogEvent('budget_entry_created');
                emit('created');
                close();
            },
            () => capturePosthogEvent('budget_entry_creation_failed')
        )
        .finally(() => {
            loading.value = false;
        });
};

defineExpose({
    open,
});
</script>
